import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckSquare, Square, X, ListPlus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import DealCard from "../components/DealCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function Bookmarks() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bundleOpen, setBundleOpen] = useState(false);
  const [bundleName, setBundleName] = useState("");
  const [bundleDesc, setBundleDesc] = useState("");
  const [bundleBusy, setBundleBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/bookmarks");
        setDeals(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, user?.bookmarks?.length]);

  if (authLoading) return <div className="max-w-7xl mx-auto px-6 py-12 text-xs font-mono uppercase tracking-widest text-black/40">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const toggleSelected = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };

  const openBundle = () => {
    if (selected.size === 0) { toast.error("Pick at least one deal to bundle."); return; }
    setBundleName("");
    setBundleDesc("");
    setBundleOpen(true);
  };

  const submitBundle = async (e) => {
    e.preventDefault();
    if (!bundleName.trim()) { toast.error("Give your wishlist a name."); return; }
    setBundleBusy(true);
    try {
      const { data } = await api.post("/wishlists", {
        name: bundleName.trim(),
        description: bundleDesc.trim() || null,
        deal_ids: Array.from(selected),
        is_public: true,
      });
      toast.success("Wishlist created — sharing link ready.");
      setBundleOpen(false);
      exitSelect();
      navigate(`/w/${data.slug}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBundleBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="bookmarks-page">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">Your library</div>
          <h1 className="font-serif font-black text-5xl md:text-6xl tracking-tighter">Saved deals</h1>
          <p className="text-black/60 mt-3">Everything you&apos;ve bookmarked, waiting to be shopped.</p>
        </div>
        {deals.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/wishlists"
              className="inline-flex items-center gap-1.5 border-2 border-black text-black text-xs font-bold uppercase tracking-[0.25em] px-4 py-2.5 hover:bg-black hover:text-white"
              data-testid="bookmarks-my-wishlists-link"
            >
              My wishlists
            </Link>
            {selectMode ? (
              <>
                <span className="text-[11px] font-mono uppercase tracking-widest text-black/60" data-testid="selected-count">
                  {selected.size} selected
                </span>
                <button
                  onClick={openBundle}
                  className="inline-flex items-center gap-2 bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-4 py-2.5 hover:bg-[#E63946] disabled:opacity-50"
                  disabled={selected.size === 0}
                  data-testid="create-wishlist-btn"
                >
                  <ListPlus className="w-4 h-4" strokeWidth={2} />
                  Bundle {selected.size > 0 ? `(${selected.size})` : ""}
                </button>
                <button
                  onClick={exitSelect}
                  className="p-2 border border-black/20 hover:border-black"
                  data-testid="exit-select-mode"
                  title="Exit selection"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </>
            ) : (
              <button
                onClick={() => setSelectMode(true)}
                className="inline-flex items-center gap-2 bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-4 py-2.5 hover:bg-[#E63946]"
                data-testid="enter-select-mode"
              >
                <ListPlus className="w-4 h-4" strokeWidth={2} />
                Bundle into wishlist
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-black/40 font-mono uppercase tracking-widest text-xs">Loading…</div>
      ) : deals.length === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center" data-testid="bookmarks-empty">
          <div className="font-serif text-3xl mb-2">Nothing saved yet</div>
          <p className="text-black/60 text-sm mb-4">Tap the bookmark icon on any deal to save it for later.</p>
          <Link to="/" className="inline-block bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]">
            Browse deals
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {deals.map((d) => {
            const isSelected = selected.has(d.id);
            return (
              <div key={d.id} className="relative">
                {selectMode && (
                  <button
                    onClick={() => toggleSelected(d.id)}
                    className={`absolute -top-2 -left-2 z-20 w-10 h-10 flex items-center justify-center border-2 ${isSelected ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white text-black border-black"}`}
                    data-testid={`select-toggle-${d.id}`}
                    aria-label={isSelected ? "Deselect" : "Select"}
                  >
                    {isSelected ? <CheckSquare className="w-5 h-5" strokeWidth={2} /> : <Square className="w-5 h-5" strokeWidth={2} />}
                  </button>
                )}
                <div className={selectMode && isSelected ? "ring-4 ring-[#E63946]" : ""}>
                  <DealCard deal={d} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={bundleOpen} onOpenChange={setBundleOpen}>
        <DialogContent className="sm:max-w-[520px] bg-white rounded-none border-2 border-black p-0" data-testid="bundle-modal">
          <DialogHeader className="border-b border-black/10 p-6 pb-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-1">New wishlist</div>
            <DialogTitle className="font-serif text-2xl font-black tracking-tight leading-tight">
              Bundle {selected.size} {selected.size === 1 ? "deal" : "deals"} into a shareable list
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitBundle} className="p-6 space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 block mb-2">Name your list</label>
              <input
                value={bundleName}
                onChange={(e) => setBundleName(e.target.value.slice(0, 80))}
                placeholder="e.g. Winter travel wishlist"
                className="w-full border border-black/20 focus:border-black outline-none px-3 py-2.5 font-serif bg-white"
                data-testid="bundle-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 block mb-2">Add a note (optional)</label>
              <textarea
                value={bundleDesc}
                onChange={(e) => setBundleDesc(e.target.value.slice(0, 280))}
                placeholder="One line for your friends about why these deals go together."
                rows={2}
                className="w-full border border-black/20 focus:border-black outline-none px-3 py-2.5 font-serif text-sm bg-white resize-none"
                data-testid="bundle-desc-input"
              />
              <div className="text-[10px] font-mono uppercase tracking-widest text-black/40 mt-1">{bundleDesc.length}/280</div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-black/10">
              <button type="button" onClick={() => setBundleOpen(false)} className="text-[10px] font-bold uppercase tracking-widest text-black/50 hover:text-[#E63946] px-3 py-2">
                Cancel
              </button>
              <button
                type="submit"
                disabled={bundleBusy}
                className="bg-[#111111] text-white text-[11px] font-bold uppercase tracking-[0.25em] px-5 py-3 hover:bg-[#E63946] disabled:opacity-50"
                data-testid="bundle-submit-btn"
              >
                {bundleBusy ? "Creating…" : "Create wishlist"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
